import { CaptureOptions } from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMount, useUnmount } from 'react-use';

import { usePreventAutoReloadOnNewVersion as usePreventAutoReload } from '@eluve/blocks';
import {
  HStack,
  Icon,
  NewButton,
  Paragraph,
  VStack,
  useToast,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';

type ReloadForNewVersionProps = {
  newAppVersion: string;
  onDismiss: (forced: boolean) => void;
  captureEvent: (
    event: string,
    meta?: Record<string, unknown>,
    options?: CaptureOptions,
  ) => void;
};

/*
  Since this component is rendered inside Toaster which is outside of AnalyticsProvider,
  we need to pass down the captureEvent function since it does not have access to the analytics context
 */
const ReloadForNewVersion: React.FC<ReloadForNewVersionProps> = ({
  newAppVersion,
  captureEvent,
  onDismiss,
}) => {
  const [reloadingCountdown, setReloadingCountdown] = useState(15);
  const intervalIdRef = useRef<number | undefined>(undefined);
  const toast = useToast();

  const handleDismiss = useCallback(
    (forced: boolean) => {
      captureEvent(`${forced ? 'force' : 'user'}_dismissed_new_version_toast`);
      onDismiss(forced);
      toast.dismiss('new-version-toast');
    },
    [captureEvent, onDismiss, toast],
  );

  const startCountdown = () => {
    intervalIdRef.current = setInterval(() => {
      const shouldPreventAutoReload = usePreventAutoReload
        .getState()
        .shouldPrevent();
      if (shouldPreventAutoReload) {
        handleDismiss(true);
        return;
      }
      setReloadingCountdown((countdown) => countdown - 1);
    }, 1000) as unknown as number;
  };

  const stopCountdown = () => {
    clearInterval(intervalIdRef.current);
  };

  useMount(() => {
    captureEvent('user_viewed_new_version_toast', { newAppVersion });
  });

  const reloadForNewVersion = useCallback(
    (auto: boolean) => {
      const shouldPreventAutoReload = usePreventAutoReload
        .getState()
        .shouldPrevent();
      if (shouldPreventAutoReload) {
        handleDismiss(true);
        return;
      }

      captureEvent(
        `${auto ? 'auto' : 'user'}_reloaded_for_new_version`,
        undefined,
        { send_instantly: true },
      );

      // Give analytics events a chance to be sent before reloading
      setTimeout(() => {
        window.location.reload();
      }, 50);
    },
    [captureEvent, handleDismiss],
  );

  useEffect(() => {
    if (reloadingCountdown === 0) {
      reloadForNewVersion(true);
    }
  }, [reloadForNewVersion, reloadingCountdown]);

  useMount(() => {
    startCountdown();
  });

  useUnmount(() => {
    stopCountdown();
  });

  return (
    <HStack
      align="start"
      gap={3}
      onMouseEnter={stopCountdown}
      onMouseLeave={startCountdown}
    >
      <Icon name="CircleFadingArrowUp" size="sm" />
      <VStack gap={0}>
        <Paragraph size="md" weight="semibold">
          New Version Available
        </Paragraph>
        <Paragraph className="text-balance" size="md" weight="regular">
          You can start using the latest version of Eluve after a quick page
          reload.
        </Paragraph>
        <HStack className="mb-1 mt-3">
          <NewButton
            onClick={() => reloadForNewVersion(false)}
            size="s"
            text={`Reload Now (${reloadingCountdown})`}
          />
          <NewButton
            onClick={() => handleDismiss(false)}
            size="s"
            text="Dismiss"
            type="outline"
          />
        </HStack>
      </VStack>
    </HStack>
  );
};

type VersionCheckerProps = {
  children: React.ReactNode;
  dismissDelayHours?: number;
  intervalMinutes?: number;
};

export const VersionChecker: React.FC<VersionCheckerProps> = ({
  children,
  dismissDelayHours = 24,
  intervalMinutes = 5,
}) => {
  const logger = useNamedLogger('VersionChecker');
  const versionRef = useRef<string | null>(null);
  const toastOpenRef = useRef<boolean>(false);
  const dismissDateRef = useRef<Date | null>(null);
  const captureEvent = useCaptureEvent();
  const toast = useToast();

  useEffect(() => {
    const checkVersion = async () => {
      const currentVersion = versionRef.current;
      const currentToastOpen = toastOpenRef.current;
      try {
        const headers = new Headers();
        if (currentVersion) {
          headers.append('If-None-Match', currentVersion);
        }
        const response = await fetch(window.location.href, {
          method: 'HEAD',
          headers,
        });

        const newVersion = response.headers.get('ETag');
        if (!newVersion) {
          return;
        }
        if (!currentVersion) {
          versionRef.current = newVersion;
          return;
        }

        const dismissDate = dismissDateRef.current;
        if (dismissDate) {
          const dismissDelayMs = dismissDelayHours * 60 * 60 * 1000;
          const timeSinceDismiss = Date.now() - dismissDate.getTime();
          if (timeSinceDismiss < dismissDelayMs) {
            return;
          }
        }

        const shouldPreventAutoReload = usePreventAutoReload
          .getState()
          .shouldPrevent();
        if (shouldPreventAutoReload) {
          logger.info(
            'Prevented prompting user to reload the page to get the latest updates',
            { newVersion, currentVersion },
          );
          return;
        }

        if (newVersion !== currentVersion && !currentToastOpen) {
          logger.info(
            'Prompting user to reload the page to get the latest updates',
            { newVersion, currentVersion },
          );

          dismissDateRef.current = null;
          toastOpenRef.current = true;

          const onDismiss = (forced: boolean) => {
            if (!forced) {
              dismissDateRef.current = new Date();
            }
            toastOpenRef.current = false;
          };

          toast.message(
            <ReloadForNewVersion
              newAppVersion={newVersion}
              onDismiss={onDismiss}
              captureEvent={captureEvent}
            />,
            {
              id: 'new-version-toast',
              position: 'bottom-right',
              duration: Infinity,
              onDismiss: () => onDismiss(false),
            },
          );
        }
      } catch (error) {
        logger.warn('Error checking version:', {
          error: (error as Error)?.message,
        });
      }
    };
    const intervalId = setInterval(checkVersion, intervalMinutes * 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [captureEvent, dismissDelayHours, intervalMinutes, logger, toast]);

  return children;
};
